import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/core/auth/AuthContext';
import { processTemplatesService } from '../services/processTemplates.service';
import type { ProcessStepInput, ProcessStepResourceInput } from '../types/processTemplate.types';

export function useProcessTemplateDetail(templateId?: string) {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const userId = profile?.id || '';
  const companyId = profile?.company_id || '';
  const queryKey = ['estimates', 'process-templates', 'detail', templateId];

  const query = useQuery({
    queryKey,
    queryFn: () => processTemplatesService.getById(templateId!),
    enabled: !!templateId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: ['estimates', 'process-templates'] });
  };

  const createStepMutation = useMutation({
    mutationFn: (input: ProcessStepInput) => processTemplatesService.createStep(userId, input),
    onSuccess: () => {
      invalidate();
      toast.success('Đã thêm bước quy trình');
    },
    onError: (error: any) => toast.error(error?.message || 'Không thêm được bước quy trình'),
  });

  const updateStepMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<ProcessStepInput> }) =>
      processTemplatesService.updateStep(userId, id, companyId, updates),
    onSuccess: () => {
      invalidate();
      toast.success('Đã cập nhật bước quy trình');
    },
    onError: (error: any) => toast.error(error?.message || 'Không cập nhật được bước quy trình'),
  });

  const deleteStepMutation = useMutation({
    mutationFn: (id: string) => processTemplatesService.deleteStep(userId, id, companyId),
    onSuccess: () => {
      invalidate();
      toast.success('Đã xóa bước quy trình');
    },
    onError: (error: any) => toast.error(error?.message || 'Không xóa được bước quy trình'),
  });

  const upsertResourcesMutation = useMutation({
    mutationFn: ({ stepId, resources }: { stepId: string; resources: ProcessStepResourceInput[] }) =>
      processTemplatesService.upsertStepResources(userId, companyId, stepId, resources),
    onSuccess: () => {
      invalidate();
      toast.success('Đã cập nhật tài nguyên bước');
    },
    onError: (error: any) => toast.error(error?.message || 'Không cập nhật được tài nguyên bước'),
  });

  return {
    template: query.data || null,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
    createStep: createStepMutation.mutateAsync,
    updateStep: updateStepMutation.mutateAsync,
    deleteStep: deleteStepMutation.mutateAsync,
    upsertStepResources: upsertResourcesMutation.mutateAsync,
    isSavingStep: createStepMutation.isPending || updateStepMutation.isPending,
    isDeletingStep: deleteStepMutation.isPending,
    isSavingResources: upsertResourcesMutation.isPending,
  };
}
